import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { URLS } from 'services/urls';
import { useGetDealFormSelectOptions } from 'components/DealForm/hooks/useGetDealFormSelectOptions';
import { useAuthContext } from 'contexts/AuthContext';
import NSButton from 'bricks/NSButton/NSButton';
import { clearLocalStorage } from 'helpers/localStorage/helpers';
import { useAsyncLocalStorage } from 'helpers/localStorage';
import { PageSpecificKeys } from 'helpers/localStorage/constants';
import { prepareBackOfEnvelopePayload } from 'views/DealDashboard/BOE/components/SlideoutForm/helpers';
import { SignUpStage, SignUpStageData, SignUpStageErrorType } from 'views/public/SignUp/SignUpContext';
import { formatStepOneData, formatStepTwoData, formatStepThreeData } from 'views/ProFormaSetup/helpers/formatStepData';
import { BOESlideoutFormValues } from 'views/DealDashboard/BOE/components/SlideoutForm/types';
import { PropertyTypeToIdMap } from 'views/DealDashboard/BOE/components/SlideoutForm/constants';
import { PRO_FORMA_TABLE_NAV } from 'views/ProFormaTable/constants';
import ProFormaSideNav from 'views/ProFormaTable/components/ProFormaSideNav/ProFormaSideNav';
import { DealType, DealTypeToId } from 'constants/deals';
import { useDealForm } from './DealForm/useDealForm';
import DealForm from './DealForm/DealForm';
import { useCreateDealFromSignup } from './hooks';
import './AccountCreated.scss';

const AccountCreated: React.FC = () => {
    const { selectedOrganizationId } = useAuthContext();
    const navigate = useNavigate();

    const [signUpStage, setSignUpStage] = useAsyncLocalStorage<SignUpStage>(
        PageSpecificKeys.SignUpStage,
        SignUpStage.ACCOUNT_CREATED,
    );
    const [signUpData, setSignUpData] = useAsyncLocalStorage<SignUpStageData>(
        PageSpecificKeys.SignUpData,
        {},
    );

    useEffect(() => {
        if (signUpStage !== SignUpStage.ACCOUNT_CREATED || !signUpData) {
            navigate(URLS.DEALS.HOME);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganizationId]);

    const {
        data: {
            marketOptions,
            constructionTypeOptions,
        },
    } = useGetDealFormSelectOptions(selectedOrganizationId);

    const quickStartData = signUpData?.[SignUpStage.PRO_FORMA_QUICK_START];

    const {
        formData,
        setFormData,
        isFormValid,
        handleChange,
    } = useDealForm({
        dealTypeId: DealTypeToId[DealType.Development],
        dealName: '',
        address: '',
        acquisitionPrice: quickStartData?.development?.acquisitionCosts || 0,
        city: '',
        county: '',
        state: '',
        zipCode: '',
        useCoordinates: false,
        market_id: '',
        constructionTypeId: '',
    });

    const { mutateAsync: createDealFromSignup, isPending: isCreatingDeal } = useCreateDealFromSignup();

    const handleSubmit = async () => {
         
        const { useCoordinates: _, ...dealData } = formData;
        const boeData = signUpData[SignUpStage.BOE_CALCULATOR] as BOESlideoutFormValues;
        const signUpDealData = { ...dealData, propertyType: boeData?.propertyType?.value || 'apartment' };
        const operationsData = quickStartData?.operations;
        const developmentData = quickStartData?.development ? { ...quickStartData.development } : undefined;
        const proFormaStartDate = quickStartData?.basicInformation?.startDate?.toISOString();
        if (developmentData?.sources) {
            developmentData.sources = developmentData.sources.map(source => ({
                ...source,
                sourceTypeId: source.sourceTypeId || source.type,
            }));
        }
        const signUpPayload = {
            dealData: signUpDealData,
            boeScenario: prepareBackOfEnvelopePayload({
                ...boeData,
                propertyType: {
                    value: PropertyTypeToIdMap[boeData?.propertyType?.value || 'apartment'],
                },
                name: 'Scenario1',
            } as BOESlideoutFormValues),
            basicInformation: formatStepOneData(
                quickStartData?.basicInformation, undefined,
                quickStartData?.basicInformation?.buildableLots?.grossBuildableAreaLots,
            ),
            development: formatStepTwoData(developmentData),
            operations: formatStepThreeData({
                operatingInfoValues: operationsData?.operatingInfoValues,
                unitArray: operationsData?.unitArray || [],
                tenantArray: operationsData?.tenantArray || [],
                incomeArray: operationsData?.incomeArray || [],
                expensesDetail: operationsData?.expensesDetail || [],
                expensesValues: operationsData?.expensesValues,
                showOtherIncomeToggle: false,
                showExpenseDetailToggle: false,
                isDraft: false,
                proFormaStartDate,
            }),
        };
        await createDealFromSignup({
            orgId: selectedOrganizationId as string,
            signUpData: signUpPayload,
        })
            .then(() => {
                clearLocalStorage(PageSpecificKeys.SignUpStage);
                clearLocalStorage(PageSpecificKeys.SignUpData);
                navigate(URLS.DEALS.HOME);
            })
            .catch(async error => {
                console.error('Error creating deal with pro forma:', error);
                await setSignUpStage(SignUpStage.ERROR);
                await setSignUpData({
                    ...signUpData,
                    [SignUpStage.ERROR]: {
                        type: SignUpStageErrorType.RESOURCE_CREATION,
                        message: error?.response?.data?.detail || error.message,
                    },
                } as SignUpStageData);
                navigate(URLS.DEALS.HOME);
            });
    };

    return (
        <div className="d-flex align-items-stretch p-3">
            <div className="flex-shrink-0 AccountCreated__side-nav">
                <ProFormaSideNav
                    activeNav={PRO_FORMA_TABLE_NAV.PRO_FORMA.name}
                    navigateToSection={() => { }}
                    hasWriteAccessToProForma={false}
                    signUpMode
                />
            </div>
            <div className="flex-grow-1 overflow-hidden ml-3">
                <div className="AccountCreated">
                    <div className="AccountCreated__container">
                        <h1>Account successfully created!</h1>
                        <p className="AccountCreated__subtitle">Now, quickly create a deal for this pro forma.</p>

                        <DealForm
                            formData={formData}
                            setFormData={setFormData}
                            marketOptions={marketOptions}
                            constructionTypeOptions={constructionTypeOptions}
                            handleChange={handleChange}
                        />

                        <NSButton
                            color="primary"
                            callback={handleSubmit}
                            disabled={!isFormValid}
                            className="w-100"
                            isLoading={isCreatingDeal}
                            dataTestId="start-using-northspyre-button"
                        >
                            Start using Northspyre Deal
                        </NSButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountCreated;
